import MessageError from '@/components/errors/message-error';
import Modal from '@/components/_ui/modal/modal';
import { cn } from '@/utils/cn';
import { TriangleAlert } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { ModalDescription, ModalHeader, ModalTitle } from '../modal/modal-header';
import { ModalContent } from '../modal/modal-content';
import { ModalCloseButton } from '../modal/modal-close-button';
import { ModalFooter } from '../modal/modal-footer';
import { ModalConfirmButton } from '../modal/modal-confirm-button';
import { Input } from '../input';
import { Spinner } from '../spinner';
import { Banner } from '../banner';

interface ModalWithConfirmationProps {
  title?: string;
  description?: string;
  bannerTitle?: string;
  confirmText?: string;
  isOpen?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onDelete?: () => void;
}

const ModalWithConfirmation = memo(
  ({
    isOpen,
    onClose,
    onDelete,
    isLoading,
    title = 'Delete Channel',
    description = 'Do you want to delete this channel?',
    bannerTitle = 'Deleting this channel will permanently delete all related data, including chat history and analytics.',
    confirmText = 'delete my channel',
  }: ModalWithConfirmationProps) => {
    const [deleteInput, setDeleteInput] = useState('');
    const [error, setError] = useState('');

    const isDeleteInputValid = useMemo(() => {
      return deleteInput === confirmText && !error;
    }, [deleteInput, error]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setDeleteInput(value);

      if (value && value !== confirmText) {
        setError('The verification text does not match.');
      } else {
        setError('');
      }
    }, []);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && isDeleteInputValid && !isLoading) {
          onDelete?.();
        }
      },
      [isDeleteInputValid, isLoading, onDelete]
    );

    return (
      <Modal
        className="p-[24px] w-[406px]"
        closeButtonClassName="top-[16px] right-[16px]"
        isButtonClose={false}
        isCloseOutside={false}
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <ModalDescription>{description}</ModalDescription>
        </ModalHeader>
        <ModalContent className="space-y-[16px]">
          <Banner icon={TriangleAlert} title={bannerTitle} variant="warning" />
          <div>
            <div className="text-[14px] font-normal leading-[20px] text-[#9D9D9C]">
              To verify, type <b className="font-bold text-[#4B4B4B]">{confirmText}</b> below:
            </div>
            <Input
              type="text"
              value={deleteInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={`Type "${confirmText}" to confirm`}
              className={cn('border rounded px-2 py-1 mt-[2px] w-full')}
              error={error}
            />
            <MessageError message={error} />
          </div>
        </ModalContent>
        <ModalFooter>
          <ModalCloseButton onClick={onClose}>Cancel</ModalCloseButton>
          <ModalConfirmButton onClick={onDelete} disabled={!isDeleteInputValid || isLoading}>
            {isLoading ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Delete
          </ModalConfirmButton>
        </ModalFooter>
      </Modal>
    );
  }
);

ModalWithConfirmation.displayName = 'ModalWithConfirmation';

export default ModalWithConfirmation;
